// import './App.css';

import { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { createPortal } from 'react-dom';
import styled, { keyframes } from 'styled-components';

import Button from '@mui/material/Button';
import ListDialog from './ListDialog.js';

const fall = keyframes`
  0% {
    transform: translate3d(0, -22vh, 0) rotate(0deg) scale(0.9);
    opacity: 0;
  }

  10% {
    opacity: 1;
  }

  100% {
    transform: translate3d(24px, 120vh, 0) rotate(720deg) scale(1.05);
    opacity: 0;
  }
`;

const RoundForm = styled(Box)`
  position: relative;
  width: 100%;
  padding-bottom: 28px;
`;

const ConfettiOverlay = styled.div`
  position: fixed;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
  z-index: 9999;
`;

const ConfettiPiece = styled.span`
  position: absolute;
  top: -12vh;
  left: ${({ $left }) => $left};
  width: ${({ $size }) => $size};
  height: ${({ $height }) => $height};
  border-radius: ${({ $shape }) => $shape};
  background: ${({ $color }) => $color};
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.22);
  opacity: 0;
  animation: ${fall} ${({ $duration }) => $duration} linear forwards;
  animation-delay: ${({ $delay }) => $delay};
`;

const StatusBar = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 4px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  z-index: 1;
`;

const StatusText = styled.div`
  font-size: 0.75rem;
  line-height: 1;
  color: rgba(0, 0, 0, 0.38);
  pointer-events: none;
`;

const RoundStatus = styled(StatusText)`
  padding-left: 4px;
`;

const ProgressStatus = styled(StatusText)`
  padding-right: 4px;
`;

const StatusLinks = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
`;

const StatusLink = styled.button`
  border: 0;
  background: transparent;
  padding: 4px 6px;
  font-size: 0.75rem;
  line-height: 1;
  color: rgba(0, 0, 0, 0.42);
  cursor: pointer;

  &:hover {
    color: rgba(0, 0, 0, 0.58);
  }
`;

const confettiPieces = [
  { left: '3%', delay: '0ms', duration: '1350ms', size: '12px', height: '24px', color: '#ff6b6b', shape: '999px' },
  { left: '8%', delay: '90ms', duration: '1480ms', size: '10px', height: '20px', color: '#ffd166', shape: '2px' },
  { left: '13%', delay: '150ms', duration: '1380ms', size: '11px', height: '22px', color: '#4ecdc4', shape: '999px' },
  { left: '18%', delay: '40ms', duration: '1440ms', size: '14px', height: '14px', color: '#5dade2', shape: '50%' },
  { left: '23%', delay: '180ms', duration: '1520ms', size: '10px', height: '22px', color: '#f28482', shape: '2px' },
  { left: '28%', delay: '70ms', duration: '1400ms', size: '12px', height: '24px', color: '#84a59d', shape: '999px' },
  { left: '33%', delay: '220ms', duration: '1500ms', size: '10px', height: '20px', color: '#f6bd60', shape: '2px' },
  { left: '38%', delay: '120ms', duration: '1420ms', size: '14px', height: '14px', color: '#cdb4db', shape: '50%' },
  { left: '43%', delay: '30ms', duration: '1360ms', size: '12px', height: '24px', color: '#90be6d', shape: '999px' },
  { left: '48%', delay: '190ms', duration: '1540ms', size: '10px', height: '20px', color: '#f94144', shape: '2px' },
  { left: '53%', delay: '60ms', duration: '1410ms', size: '12px', height: '22px', color: '#577590', shape: '999px' },
  { left: '58%', delay: '240ms', duration: '1490ms', size: '14px', height: '14px', color: '#ffb703', shape: '50%' },
  { left: '63%', delay: '100ms', duration: '1450ms', size: '10px', height: '22px', color: '#fb8500', shape: '2px' },
  { left: '68%', delay: '20ms', duration: '1370ms', size: '12px', height: '24px', color: '#219ebc', shape: '999px' },
  { left: '73%', delay: '170ms', duration: '1510ms', size: '14px', height: '14px', color: '#ffafcc', shape: '50%' },
  { left: '78%', delay: '80ms', duration: '1430ms', size: '10px', height: '20px', color: '#8ecae6', shape: '2px' },
  { left: '83%', delay: '210ms', duration: '1530ms', size: '12px', height: '22px', color: '#bde0fe', shape: '999px' },
  { left: '88%', delay: '50ms', duration: '1390ms', size: '14px', height: '14px', color: '#d9ed92', shape: '50%' },
  { left: '93%', delay: '130ms', duration: '1470ms', size: '10px', height: '22px', color: '#ff99c8', shape: '2px' },
  { left: '97%', delay: '250ms', duration: '1550ms', size: '12px', height: '24px', color: '#a0c4ff', shape: '999px' }
];

function Round ({
  round,
  onPick,
  isCelebrating = false,
  currentRound = 0,
  currentRoundNamesIndex = 0,
  totalRoundNameCount = 0,
  names = []
}) {
  const [isListOpen, setIsListOpen] = useState(false);
  const [isManualConfettiVisible, setIsManualConfettiVisible] = useState(false);
  const manualConfettiTimeoutRef = useRef(null);
  const isConfettiVisible = isCelebrating || isManualConfettiVisible;

  useEffect(() => {
    return () => {
      if (manualConfettiTimeoutRef.current) {
        clearTimeout(manualConfettiTimeoutRef.current);
      }
    };
  }, []);

  const handlePlayConfetti = () => {
    setIsManualConfettiVisible(false);

    if (manualConfettiTimeoutRef.current) {
      clearTimeout(manualConfettiTimeoutRef.current);
    }

    requestAnimationFrame(() => {
      setIsManualConfettiVisible(true);
      manualConfettiTimeoutRef.current = setTimeout(() => {
        setIsManualConfettiVisible(false);
        manualConfettiTimeoutRef.current = null;
      }, 1400);
    });
  };

  return (<RoundForm
    component="form"
    fullWidth
  >
    {isConfettiVisible && createPortal(
      <ConfettiOverlay aria-hidden="true">
        {confettiPieces.map((piece) => (
          <ConfettiPiece
            key={`${piece.left}-${piece.delay}-${isCelebrating ? 'round' : 'manual'}`}
            $left={piece.left}
            $delay={piece.delay}
            $duration={piece.duration}
            $size={piece.size}
            $height={piece.height}
            $color={piece.color}
            $shape={piece.shape}
          />
        ))}
      </ConfettiOverlay>,
      document.body
    )}

    {round.map((name) => {
        return (<Button variant="contained" key={name.name} value={name.name} onClick={onPick} disabled={isCelebrating} fullWidth sx={{ m: 1 }}>{name.name}</Button>);
    })}

    <Button key={null} value={null} onClick={onPick} disabled={isCelebrating} fullWidth sx={{ m: 1 }}>Skip</Button>

    <StatusBar>
      <RoundStatus>{`Round: ${currentRound}`}</RoundStatus>
      <StatusLinks>
        <StatusLink type="button" onClick={() => setIsListOpen(true)}>Top Names</StatusLink>
        <StatusLink type="button" onClick={handlePlayConfetti}>Confetti</StatusLink>
      </StatusLinks>
      <ProgressStatus>{`${currentRoundNamesIndex} / ${totalRoundNameCount}`}</ProgressStatus>
    </StatusBar>
    <ListDialog open={isListOpen} setOpen={setIsListOpen} list={names}/>

  </RoundForm>);
}

export default Round;
